import { Grid, TextField, Typography } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import FormSection from "../../../common/components/functional/formSection/FormSection";
import {
  CoverLetter,
  Talent,
  talentIsDeactivated,
} from "../../talents/talentAPI";
import {
  selectTalentById,
  updateTalentCoverLetter,
} from "../../talents/talentsSlice";
import { ContentKey } from "../TalentDetails";
import TalentRecommenders from "./TalentRecommenders";
import { AccessRightContext } from "../../user/AccessRightProvider";
import { UnsavedChangesProvider } from "../../../contexts/UnsavedChangesContext";

type Props = {
  handleOpenEditModal: (content: ContentKey, id?: string) => void;
};

const TalentCoverLetter = ({ handleOpenEditModal }: Props) => {
  const [edit, setEdit] = useState<boolean>(false);
  const { t } = useTranslation(["talentProfile", "common"]);
  const { talentId } = useParams();
  const talent = useAppSelector(
    (state) => selectTalentById(state, talentId as string) as Talent,
  );

  const { canEditTalentProfile } = useContext(AccessRightContext);

  const dispatch = useAppDispatch();

  const defaultValues = {
    value: talent.coverLetter?.value || "",
    version: talent.coverLetter?.version || 0,
  };

  const methods = useForm<CoverLetter>({
    defaultValues,
    mode: "onChange",
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty },
  } = methods;

  // Reset form values when talent.coverLetter changes
  useEffect(() => {
    reset({
      value: talent?.coverLetter?.value || "",
      version: talent?.coverLetter?.version || 0,
    });
  }, [talent?.coverLetter, reset]);

  const onEdit = () => {
    if (!edit) setEdit(true);
    if (edit) return handleSubmit(onSubmit)();
  };

  const onCancel = () => {
    setEdit(false);
    reset(defaultValues);
  };

  const onSubmit = (coverLetter: CoverLetter) => {
    if (talentId) {
      dispatch(
        updateTalentCoverLetter({
          talentId: talentId,
          coverLetter: coverLetter.value,
          version: defaultValues.version,
        }),
      ).then((action) => isFulfilled(action) && setEdit(false));
    }
  };

  return (
    <FormSection
      title={t("talentProfile:coverLetterTitle")}
      margin
      onEdit={onEdit}
      onCancel={onCancel}
      saveDisabled={!isDirty}
      edit={edit}
      readOnly={!canEditTalentProfile || talentIsDeactivated(talent)}
    >
      <Grid
        item
        container
        xs={3}
        justifyContent="flex-end"
        sx={{ marginTop: "8px" }}
      >
        <Typography textAlign="end" variant="secondary">
          {t("talentProfile:talentCoverLetter")}
        </Typography>
      </Grid>
      <Grid item justifyContent="flex-start" alignItems="left" container xs={7}>
        <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
          <Controller
            name="value"
            control={control}
            render={({ field: { name, value, onChange } }) =>
              edit ? (
                <>
                  <TextField
                    name={name}
                    size="small"
                    fullWidth
                    multiline
                    minRows={4}
                    maxRows={10}
                    value={value}
                    onChange={(event) =>
                      event.target.value.length <= 2000 && onChange(event)
                    }
                  />

                  <Typography
                    sx={{ marginLeft: "auto" }}
                    variant="body2"
                    color="textSecondary"
                  >
                    {value.length + "/2000"}
                  </Typography>
                </>
              ) : (
                <Typography>{value}</Typography>
              )
            }
          />
        </UnsavedChangesProvider>
      </Grid>
      <TalentRecommenders
        recommenders={talent.recommenders}
        handleOpenEditModal={handleOpenEditModal}
      />
    </FormSection>
  );
};

export default TalentCoverLetter;
